/**
 * useModalChrome — shared accessibility + scroll-lock behaviour for modals.
 *
 * Solves three problems that every modal in the app previously had to (and
 * mostly didn't) solve for itself:
 *
 * 1. **Escape closes the modal.** Standard ARIA dialog contract. Critical
 *    for keyboard + Bluetooth-keyboard users (Samsung DeX, tablet covers).
 * 2. **Body scroll lock.** Without it, momentum scroll inside the modal
 *    bleeds through to the underlying map — opening AuthModal and flicking
 *    pans Leaflet behind the dialog, and on iOS the page scrolls too.
 * 3. **Focus trap.** Tab / Shift+Tab cycle within the modal so screen-reader
 *    + keyboard users can't escape to background controls.
 *
 * Usage:
 *
 *   const modalRef = useRef<HTMLDivElement>(null);
 *   useModalChrome({ isOpen: true, onClose, modalRef });
 *   return <div ref={modalRef} role="dialog" aria-modal="true">…</div>;
 *
 * If you don't pass `modalRef`, only Escape + scroll-lock are wired; focus
 * trap is a no-op. Most modals can omit the ref unless they have form
 * fields users tab through.
 */

import { useEffect, type RefObject } from 'react';

interface UseModalChromeOptions {
  /** Whether the modal is currently mounted/open. */
  isOpen: boolean;
  /** Called when Escape is pressed. Wrap in `useCallback` upstream if its
   *  identity changes per render — otherwise the listener re-attaches. */
  onClose: () => void;
  /** Optional ref to the modal root for focus trapping. */
  modalRef?: RefObject<HTMLElement>;
  /** Set to true to skip focus trap (e.g. for transient toasts). */
  disableFocusTrap?: boolean;
}

/** Selector matching everything the browser considers tab-stoppable. */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function useModalChrome({
  isOpen,
  onClose,
  modalRef,
  disableFocusTrap = false,
}: UseModalChromeOptions): void {
  // Escape key handler
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  // Focus trap. Activates only when modalRef is provided.
  useEffect(() => {
    if (!isOpen || disableFocusTrap || !modalRef?.current) return;
    const root = modalRef.current;
    const previousActiveElement = document.activeElement as HTMLElement | null;

    // Focus the first focusable element in the modal on mount so keyboard
    // users land inside it instead of in stale background focus.
    const focusables = root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusables.length > 0) {
      // Small delay to let the modal finish entering its animation; without
      // it, Safari sometimes fails to scroll the focused element into view.
      requestAnimationFrame(() => focusables[0]?.focus());
    }

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = Array.from(
        root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleTab);
    return () => {
      document.removeEventListener('keydown', handleTab);
      // Restore focus to whatever opened the modal, so keyboard users
      // don't get stranded at <body>.
      previousActiveElement?.focus?.();
    };
  }, [isOpen, disableFocusTrap, modalRef]);
}
